import * as React from 'react'

import { Box, Button, Hint, Section } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import AutoCompleteSearchField from 'modules/core/components/AutoCompleteSearchField'
import FormRow from 'modules/core/components/FormRow'
import { GoogleMapsContext } from 'modules/core/components/GoogleMaps/GoogleMapsProvider'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import countryList from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { timezoneOptions } from 'modules/core/lib/timezones'
import app from 'modules/core/store/models/app'

import attendancePolicies from 'modules/attendance/store/collections/policies'
import AttendanceSettings from 'modules/attendance/store/collections/settings'
import EditLocationButton from 'modules/locations/components/GoogleMaps/Buttons/EditLocationButton'
import MapSidePanel from 'modules/locations/components/GoogleMaps/MapSidePanel'
import StaticMap from 'modules/locations/components/GoogleMaps/StaticMap'
import { useAutoCompleteAddress } from 'modules/locations/hooks/useAutocompleteAddress'
import { TLocationsLocation, useLocations } from 'modules/locations/hooks/useLocations'
import { useUpdateLocation } from 'modules/locations/hooks/useUpdateLocation'

interface EditWorkplaceFormValues {
  id: string
  name: string
  phoneNumber?: string
  addressLineOne: string
  addressLineTwo?: string
  country: string
  timezone: string
  postalCode: string
  state: string
  city: string
  latitude?: number
  longitude?: number
  radius?: number
  siret?: string
}
interface Props {
  locationId: string
}
const EditLocationWorkplaceForm = ({ locationId }: Props) => {
  const { locations } = useLocations()
  const location = locations.find((item: TLocationsLocation) => item.id === locationId)!
  const { updateLocation } = useUpdateLocation()
  const [showConfirmAddressPopover, setShowConfirmAddressPopover] = React.useState<boolean>(false)

  const { featureAvailability: hasGeofencingFeatureAvailability } = useFeatureAvailability(
    Features.time_tracking_geofencing_alerts
  )
  const hasGeofencingFeature = hasGeofencingFeatureAvailability.available
  const hasGeoBlockingFeature = hasFeature('dev_time_tracking_geofencing_block')
  const showGelocation = hasGeofencingFeature || hasGeoBlockingFeature

  useAsyncData(() => {
    if (!showGelocation) return Promise.resolve()

    return Promise.all([AttendanceSettings.fetch(), attendancePolicies.fetch()])
  }, [showGelocation])

  const policiesId = AttendanceSettings.policyIdWithGeolocation()

  const hasGeoLocation = showGelocation && attendancePolicies.hasEmployees(policiesId)
  const inputRef = useLazyRef<HTMLInputElement | null>(() => null)
  const [showMapPanel, setShowMapPanel] = React.useState<boolean>(false)

  const workplaceForm = useForm<EditWorkplaceFormValues>({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    defaultValues: {
      id: locationId,
      name: location?.name ?? '',
      phoneNumber: location?.phoneNumber ?? '',
      addressLineOne: location?.addressLine1 ?? '',
      addressLineTwo: location?.addressLine2 ?? '',
      country: location?.country ?? '',
      timezone: location?.timezone ?? '',
      postalCode: location?.postalCode ?? '',
      state: location?.state ?? '',
      city: location?.city ?? '',
      latitude: location?.latitude ?? undefined,
      longitude: location?.longitude ?? undefined,
      radius: location?.radius ?? 0,
      siret: location?.siret ?? '',
    },
    validate: (data) => {
      const errors: FormErrors<EditWorkplaceFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.empty')
      }

      if (!data.timezone) {
        errors.timezone = i18n.t('common.empty')
      }

      if (!data.country) {
        errors.country = i18n.t('common.empty')
      }

      if (hasGeoLocation) {
        if (!data.addressLineOne) {
          errors.addressLineOne = i18n.t('common.empty')
        }
        if (!data.postalCode) {
          errors.postalCode = i18n.t('common.empty')
        }
        if (!data.state) {
          errors.state = i18n.t('common.empty')
        }
        if (!data.city) {
          errors.city = i18n.t('common.empty')
        }
        if (!data.radius) {
          errors.radius = i18n.t('common.empty')
        }
      }

      return errors
    },
    async onSubmit(data, { setErrors }) {
      const result = await updateLocation({ ...data, radius: Number(data.radius) })
      const handled = handleResult(result, (errors) => {
        setErrors({ ...errors })
      })
      setFlashMessage(handled.success)
      return handled
    },
  })
  const {
    addressLineOne,
    addressLineTwo,
    city,
    country,
    latitude,
    longitude,
    name,
    phoneNumber,
    postalCode,
    radius,
    siret,
    state,
    timezone,
  } = workplaceForm.values
  const { placesLibraryLoaded } = React.useContext(GoogleMapsContext)

  const { place, suggestions, onPlaceSelected } = useAutoCompleteAddress({
    inputRef,
    placesLibraryLoaded,
  })

  useEffect(() => {
    if (!place) return

    workplaceForm.setFieldValue('addressLineOne', place.address)
    workplaceForm.setFieldValue('city', place.city)
    workplaceForm.setFieldValue('postalCode', place.postcode)
    workplaceForm.setFieldValue('state', place.state)
    workplaceForm.setFieldValue('country', place.country)
    workplaceForm.setFieldValue('timezone', place.timeZone)
    workplaceForm.setFieldValue('latitude', place.lat)
    workplaceForm.setFieldValue('longitude', place.lng)
  }, [place?.address])

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success ? i18n.t('common.saved_successfully') : i18n.t('errors.generic_error')
    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const onMarkerChange = (lat: number | undefined, lng: number | undefined) => {
    workplaceForm.setFieldValue('latitude', lat)
    workplaceForm.setFieldValue('longitude', lng)
  }

  const showMapSidePanel = () => setShowMapPanel(true)
  const hideMapSidePanel = () => setShowMapPanel(false)

  const editLocationButton = (
    <EditLocationButton lat={latitude!} lng={longitude!} onClick={showMapSidePanel} />
  )

  const frenchCompany = country == 'fr' || location?.country == 'fr'

  return (
    <Section
      illustration="location-building-pin"
      title={i18n.t('settings.locations.add.section_title')}
      description={i18n.t('settings.locations.add.section_description')}
    >
      <FormRow extra={{ 'data-intercom-target': 'settings-location-workplaces-form' }}>
        <Box background="grey200" borderRadius={{ all: 'abs008' }} padding="s32" gap="s12">
          <TextField
            label={i18n.t('settings.locations.add.form.name')}
            value={name}
            onChange={(value) => workplaceForm.setFieldValue('name', value)}
            mandatory
            error={workplaceForm.errors.name}
          />
          <TextField
            label={i18n.t('settings.locations.add.form.phone')}
            value={phoneNumber}
            onChange={(value) => workplaceForm.setFieldValue('phoneNumber', value)}
          />
          {frenchCompany && (
            <TextField
              label={i18n.t('settings.locations.add.form.siret')}
              value={siret}
              onChange={(value) => workplaceForm.setFieldValue('siret', value)}
            />
          )}
          {hasGeoLocation ? (
            <>
              <AutoCompleteSearchField
                label={i18n.t('settings.locations.add.form.address')}
                suggestions={suggestions}
                value={addressLineOne}
                onSelect={({ value, label }: { value: string; label: string }) => {
                  workplaceForm.setFieldValue('addressLineOne', label)
                  onPlaceSelected(value)
                  setShowConfirmAddressPopover(true)
                }}
                getRef={(ref) => (ref ? (inputRef.current = ref) : null)}
                onChange={(value) => workplaceForm.setFieldValue('addressLineOne', value)}
                mandatory={hasGeoLocation}
                icon="pin"
                error={workplaceForm.errors.addressLineOne}
              />
              {(!latitude || !longitude) && (
                <Box>
                  <Hint.Popover.Provider steps={1}>
                    <Hint.Popover
                      step={1}
                      title={i18n.t('settings.locations.add.form.popover.title')}
                      description={i18n.t('settings.locations.add.form.popover.description')}
                      actionLabel={i18n.t('common.close')}
                      position="left top"
                    ></Hint.Popover>
                  </Hint.Popover.Provider>
                </Box>
              )}
            </>
          ) : (
            <TextField
              label={i18n.t('settings.locations.add.form.address')}
              value={addressLineOne}
              onChange={(value) => workplaceForm.setFieldValue('addressLineOne', value)}
            />
          )}
          <>
            <TextField
              label={i18n.t('settings.locations.add.form.address_add_info')}
              value={addressLineTwo}
              onChange={(value) => workplaceForm.setFieldValue('addressLineTwo', value)}
            />
            <Box flexDirection="row" gap="s12">
              <TextField
                label={i18n.t('settings.locations.add.form.city')}
                value={city}
                onChange={(value) => workplaceForm.setFieldValue('city', value)}
                mandatory={hasGeoLocation}
                autofilled={!!place?.city && place.city === city}
                error={workplaceForm.errors.city}
              />
              <TextField
                label={i18n.t('settings.locations.add.form.zip_code')}
                value={postalCode}
                onChange={(value) => workplaceForm.setFieldValue('postalCode', value)}
                mandatory={hasGeoLocation}
                autofilled={!!place?.postcode && place.postcode === postalCode}
                error={workplaceForm.errors.postalCode}
              />
            </Box>
            <TextField
              label={i18n.t('settings.locations.add.form.region')}
              value={state}
              onChange={(value) => workplaceForm.setFieldValue('state', value)}
              mandatory={hasGeoLocation}
              autofilled={!!place?.state && place.state === state}
              error={workplaceForm.errors.state}
            />
            <Select
              none={i18n.t('common.select')}
              onChange={(value) => workplaceForm.setFieldValue('country', value)}
              value={country}
              label={i18n.t('settings.locations.add.form.country')}
              options={countryList}
              mandatory
              autofilled={!!place?.country && place.country == country}
              error={workplaceForm.errors.country}
            />
            <Select
              mandatory
              none={i18n.t('common.select')}
              value={timezone}
              label={i18n.t('settings.locations.add.form.time_zone')}
              onChange={(value) => workplaceForm.setFieldValue('timezone', value)}
              options={timezoneOptions()}
              autofilled={!!place?.timeZone && place.timeZone === timezone}
              error={workplaceForm.errors.timezone}
            />
            {hasGeoLocation && (
              <Select
                onChange={(value) => workplaceForm.setFieldValue('radius', value)}
                label={i18n.t('settings.locations.map.radius_label')}
                value={radius?.toString()}
                error={workplaceForm.errors.radius}
                none={i18n.t('common.choose')}
                options={[
                  {
                    value: 50,
                    label: i18n.t('settings.locations.map.radius_options.within_less_one_block'),
                  },
                  {
                    value: 100,
                    label: i18n.t('settings.locations.map.radius_options.within_one_block'),
                  },
                  {
                    value: 500,
                    label: i18n.t('settings.locations.map.radius_options.within_few_blocks'),
                  },
                  {
                    value: 1000,
                    label: i18n.t('settings.locations.map.radius_options.within_huge_perimeter'),
                  },
                ]}
              />
            )}
          </>
          {hasGeoLocation && latitude && longitude && (
            <StaticMap
              lat={latitude}
              lng={longitude}
              editLocationButton={editLocationButton}
              radius={radius || 0}
              showPopover={showConfirmAddressPopover}
              onPopupClose={() => setShowConfirmAddressPopover(false)}
            />
          )}
          <Box alignItems="flexEnd">
            <Button
              onClick={() => workplaceForm.handleSubmit()}
              extra={{ 'data-intercom-target': 'settings-location-workplace-form-submit' }}
            >
              {i18n.t('settings.locations.add.form.submit')}
            </Button>
          </Box>
        </Box>
      </FormRow>
      <MapSidePanel
        show={showMapPanel}
        onClose={hideMapSidePanel}
        lat={latitude!}
        lng={longitude!}
        radius={radius!}
        onMarkerChange={onMarkerChange}
      />
    </Section>
  )
}

export default observer(EditLocationWorkplaceForm)
